// Pure helpers for the onboarding flow (OnboardingFlow.jsx).

// Every step the flow walks, in order. The flow's transitions are written
// in OnboardingFlow, so the progress dots follow this list rather than the
// admin-editable copy: the saved copy listed no `notify` (and the app's
// fallback no `build`), so no dot lit on those steps.
export const ONBOARDING_STEPS = [
  'welcome',
  'intent',
  'water',
  'subtype',
  'size',
  'age',
  'photo',
  'build',
  'first-read',
  'notify',
];

// Where Back goes. "I'm not sure yet" on the water step skips the style
// step, so Back from size returns to the water question instead of a
// style list for a water type the keeper never picked.
export function previousStep(step, progress, steps = ONBOARDING_STEPS) {
  const i = steps.indexOf(step);
  if (i <= 0) return null;
  const prev = steps[i - 1];
  if (prev === 'subtype' && progress?.water_type === 'unsure') return 'water';
  return prev;
}

// Mailbox names that are not a person's name.
const NOT_A_NAME = new Set([
  'admin',
  'contact',
  'hello',
  'hi',
  'info',
  'mail',
  'me',
  'noreply',
  'office',
  'reef',
  'sales',
  'support',
  'team',
  'test',
]);

// A first name from the sign-in address, or '' when the address does not
// start with one. "jsmith1987" used to greet "J smith1987"; a wrong name is
// worse than none.
export function welcomeName(email) {
  const local = String(email || '').split('@')[0];
  if (!local) return '';
  const first = local.split(/[._-]+/)[0] || '';
  if (!/^\p{L}{2,20}$/u.test(first)) return '';
  if (NOT_A_NAME.has(first.toLowerCase())) return '';
  return first.charAt(0).toUpperCase() + first.slice(1).toLowerCase();
}

// A save worth one more try: the connection dropped, or the server was
// mid-deploy (Railway answers 502 while the new copy starts). A 4xx is the
// answer itself, so it is not retried.
export function isRetryableSaveError(e) {
  if (!e) return false;
  if (e.name === 'AbortError') return false;
  if (e.status == null) return true;
  return e.status >= 500;
}

// Runs `fn`, and once more after `delayMs` when the first try failed in a
// way worth retrying. Onboarding saves are merges (PATCH) or claim-guarded
// (complete answers 409 to a second call), so a repeat is safe.
export async function withOneRetry(fn, { delayMs = 1500, sleep } = {}) {
  try {
    return await fn();
  } catch (e) {
    if (!isRetryableSaveError(e)) throw e;
    await (sleep ? sleep(delayMs) : new Promise((r) => setTimeout(r, delayMs)));
    return await fn();
  }
}

// What the keeper reads when a save still failed after the retry.
export function saveErrorMessage(e, fallback = 'Could not save your answer.') {
  if (isRetryableSaveError(e)) {
    return "Couldn't save that just now. Your answer is still here: check your connection and tap it again.";
  }
  return e?.message || fallback;
}

// The welcome line for a keeper someone shared a tank with. `shared` is
// GET /onboarding's list of { tank_name, owner_email }.
export function sharedWelcome(shared, count = 0) {
  const list = Array.isArray(shared) ? shared : [];
  const n = Math.max(Number(count) || 0, list.length);
  if (n <= 0) return null;
  if (n === 1) {
    const t = list[0] || {};
    const who = t.owner_email || 'Someone';
    const what = t.tank_name ? `"${t.tank_name}"` : 'a tank';
    return { heading: `${who} shared ${what} with you.`, cta: 'Go to the shared tank' };
  }
  const owners = [...new Set(list.map((t) => t.owner_email).filter(Boolean))];
  const heading =
    owners.length === 1
      ? `${owners[0]} shared ${n} tanks with you.`
      : `${n} tanks have been shared with you.`;
  return { heading, cta: 'Go to the shared tanks' };
}
